import React from "react";
import { Play, Sparkles, MapPin, Navigation, Star, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import AppBadges from "./AppBadges";
import Countdown from "./Countdown";
import { CONTACT, LAUNCH_LABEL } from "../mock";

function PhoneMockup() {
  return (
    <div className="relative mx-auto w-[300px] sm:w-[330px] id-float">
      {/* ambient glow */}
      <div className="absolute -inset-10 bg-[#2fd645]/20 blur-3xl rounded-full" />
      <div className="relative rounded-[2.6rem] border-[10px] border-[#12181500] ring-1 ring-white/10 bg-[#0c110f] p-3 shadow-2xl id-glow">
        <div className="rounded-[2rem] overflow-hidden bg-[#0a0f0c] relative h-[560px]">
          {/* notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 h-6 w-32 bg-black rounded-b-2xl" />
          {/* map */}
          <div className="absolute inset-0 id-grid-bg opacity-60" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(400px circle at 60% 35%, rgba(47,214,69,0.18), transparent 60%)" }} />
          {/* fake routes */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 330 560" fill="none">
            <path d="M-20 120 L120 200 L200 160 L360 240" stroke="#1d2a22" strokeWidth="10" />
            <path d="M40 620 L120 380 L210 300 L200 160" stroke="#1d2a22" strokeWidth="14" />
            <path d="M40 620 L120 380 L210 300 L200 160" stroke="#2fd645" strokeWidth="4" strokeDasharray="2 10" strokeLinecap="round" />
          </svg>
          {/* pins */}
          <div className="absolute" style={{ top: "150px", left: "185px" }}>
            <div className="relative">
              <div className="relative h-4 w-4 rounded-full bg-[#2fd645] id-pulse-ring" />
            </div>
          </div>
          <div className="absolute" style={{ top: "360px", left: "105px" }}>
            <MapPin className="h-8 w-8 text-white drop-shadow-lg" fill="#2fd645" />
          </div>
          {/* top status bar */}
          <div className="absolute top-9 inset-x-4 z-10">
            <div className="rounded-2xl bg-black/55 backdrop-blur-md border border-white/10 px-4 py-3 flex items-center gap-3">
              <Navigation className="h-4 w-4 text-[#2fd645]" />
              <span className="text-xs text-white/80">Driver arriving in <b className="text-white">3 min</b></span>
            </div>
          </div>
          {/* bottom sheet */}
          <div className="absolute bottom-0 inset-x-0 z-10">
            <div className="rounded-t-3xl bg-[#0e1512] border-t border-white/10 p-5 space-y-4">
              <div className="h-1 w-10 bg-white/20 rounded-full mx-auto" />
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-[#2fd645]/15 flex items-center justify-center border border-[#2fd645]/30">
                  <span className="font-display font-extrabold text-[#2fd645]">A</span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white">Adewale · Toyota Corolla</div>
                  <div className="flex items-center gap-1 text-xs text-white/60">
                    <Star className="h-3 w-3 text-[#2fd645]" fill="#2fd645" /> 4.9 · GEA-482-KJ
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-white/50">Instant Comfort</div>
                  <div className="text-lg font-bold text-[#2fd645]">₦2,400</div>
                </div>
              </div>
              <button className="w-full rounded-xl bg-[#2fd645] text-[#06210b] font-bold py-3 text-sm">
                Confirm pickup
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Hero({ onWaitlist, onVideo }) {
  return (
    <section id="top" className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
      <div className="absolute inset-0 id-radial" />
      <div className="absolute inset-0 id-grid-bg opacity-30" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#2fd645]/30 bg-[#2fd645]/10 px-4 py-1.5 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#2fd645] opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#2fd645]" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-widest text-[#2fd645]">
                Launching in Nigeria &mdash; a proudly indigenous company
              </span>
            </div>

            <h1 className="font-display font-extrabold text-white leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
              Ride Smart.
              <br />
              <span className="italic-display text-[#2fd645] id-text-glow">Go Further.</span>
            </h1>

            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              InstantDrive is Nigeria&apos;s smarter way to move. Tap to book a
              verified driver, see an upfront price, track your ride live and pay
              cashless — all in under a minute.
            </p>

            <div className="mt-7">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">
                Launching {LAUNCH_LABEL}
              </div>
              <Countdown compact />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button
                onClick={onWaitlist}
                className="h-14 px-8 text-base bg-[#2fd645] text-[#06210b] font-bold hover:bg-[#46ff5c] rounded-xl transition-transform hover:scale-[1.03]"
              >
                Register now
              </Button>
              <Button
                onClick={onVideo}
                variant="outline"
                className="h-14 px-8 text-base rounded-xl border-white/25 bg-white/5 text-white hover:bg-white/10 hover:border-[#2fd645]"
              >
                <Play className="mr-2 h-5 w-5 text-[#2fd645]" fill="currentColor" /> Watch how it works
              </Button>
            </div>

            <div className="mt-8">
              <AppBadges />
            </div>

            <div className="mt-8 flex items-center gap-6 text-sm text-white/55">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#2fd645]" /> Verified drivers</div>
              <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#2fd645]" /> No surge shocks</div>
            </div>
          </div>

          <div className="relative">
            <PhoneMockup />
          </div>
        </div>
      </div>
    </section>
  );
}
